/**
 * gl-filters.js -- the "advanced video" path: real GPU pixel-art upscaling
 * shaders (xBR, xBRZ, Sabr, ScaleFX), ported to WebGL2 from their reference
 * GLSL source (see shaders/VENDORING.md for provenance and licensing --
 * xbrz-freescale and sabr are GPL, unlike the rest of this codebase).
 *
 * This module is only ever touched when a user opts into the "Advanced
 * video (WebGL)" toggle in main.js; the default video path (pixelated /
 * smooth / sharp-bilinear in main.js's presentFrame()) never imports it.
 *
 * How a RetroArch-style shader file becomes a WebGL2 program: each .glsl
 * file wraps both stages in one file, selected via `#if defined(VERTEX)` /
 * `#elif defined(FRAGMENT)`. buildProgram() strips the file's own
 * `#version 130` (desktop GLSL) and recompiles the body twice, once with
 * `#define VERTEX` and once with `#define FRAGMENT`, each prefixed with
 * `#version 300 es` -- the same trick RetroArch's own loader uses. Because
 * WebGL2 is always an ES profile, `GL_ES` is defined, which makes these
 * shaders take their "direct texture lookup" code path rather than the
 * desktop `textureOffset` path -- both exist in every file for exactly this
 * portability reason.
 *
 * ScaleFX is a 5-pass pipeline (metric data -> strength -> junction
 * resolution -> edge tagging -> final composite) and needs floating-point
 * intermediate render targets (pass 0/1 store unbounded colour-distance
 * metrics, not colours), which requires the EXT_color_buffer_float
 * extension. If it's unavailable, ScaleFX is reported unavailable via
 * isFilterReady()/failureFor() rather than silently rendering garbage.
 */

import xbrSrc from './shaders/xbr-lv2.glsl?raw';
import xbrzSrc from './shaders/xbrz-freescale.glsl?raw';
import sabrSrc from './shaders/sabr-v3.0.glsl?raw';
import sfx0Src from './shaders/scalefx-pass0.glsl?raw';
import sfx1Src from './shaders/scalefx-pass1.glsl?raw';
import sfx2Src from './shaders/scalefx-pass2.glsl?raw';
import sfx3Src from './shaders/scalefx-pass3.glsl?raw';
import sfx4Src from './shaders/scalefx-pass4.glsl?raw';

export const ADVANCED_FILTERS = [
  { id: 'xbr', label: 'xBR (level 2)' },
  { id: 'xbrz', label: 'xBRZ (freescale)' },
  { id: 'sabr', label: 'Sabr' },
  { id: 'scalefx', label: 'ScaleFX (fixed 3x)' },
];

const IDENTITY4 = new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);

function stripVersionLine(src) {
  return src.replace(/^\s*#version[^\n]*\n/, '');
}

/**
 * Tiny, surgical fixes for real incompatibilities between GLSL ES 3.00
 * (what WebGL2 requires) and the desktop-GLSL-1.30-oriented style these
 * shaders were written in. Nothing here changes behaviour -- it makes
 * already-true things explicit enough for the stricter ES validator.
 * Applied only in memory at load time; the vendored files on disk stay
 * byte-identical to upstream (see shaders/VENDORING.md).
 */
const ES300_PATCHES = {
  // xbr-lv2.glsl initializes `delta_u` from another non-const global
  // (`delta_l.yxwz`). Desktop GLSL 1.30 accepts that; GLSL ES 3.00 requires
  // global initializers to be constant expressions, and a plain (non-const)
  // global doesn't qualify even when its own initializer happens to be
  // constant. `delta`/`delta_l` are computed purely from the #define
  // XBR_SCALE, so marking them `const` makes `delta_u`'s initializer a
  // genuine compile-time constant and satisfies the rule with no change in
  // value.
  xbr: [
    { find: /^vec4 delta {3}= /m, replace: 'const vec4 delta   = ' },
    { find: /^vec4 delta_l = /m, replace: 'const vec4 delta_l = ' },
  ],
};

function applyEs300Patches(label, body) {
  let patched = body;
  for (const { find, replace } of ES300_PATCHES[label] ?? []) patched = patched.replace(find, replace);
  return patched;
}

function compileShader(gl, type, source, label) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`[${label}] ${type === gl.VERTEX_SHADER ? 'vertex' : 'fragment'} shader failed:\n${log}`);
  }
  return shader;
}

function buildProgram(gl, rawSource, label) {
  const body = applyEs300Patches(label, stripVersionLine(rawSource));
  // GLSL ES 3.00 requires a precision for every float-based declaration,
  // including `out` variables, from the point they're declared onward. Some
  // of these files declare `out vec4 FragColor;` before their own `#ifdef
  // GL_ES precision ...` block (harmless under desktop GLSL, which has no
  // such requirement) -- a default set here first covers that gap; the
  // shader's own later precision statement still takes over from there.
  const vs = compileShader(gl, gl.VERTEX_SHADER, `#version 300 es\n#define VERTEX\nprecision mediump float;\n${body}`, label);
  const fs = compileShader(
    gl,
    gl.FRAGMENT_SHADER,
    `#version 300 es\n#define FRAGMENT\nprecision mediump float;\n${body}`,
    label,
  );

  const program = gl.createProgram();
  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);
  gl.deleteShader(vs);
  gl.deleteShader(fs);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(`[${label}] program link failed:\n${log}`);
  }
  return program;
}

function reflectUniforms(gl, program) {
  const uniforms = {};
  const count = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
  for (let i = 0; i < count; i++) {
    const info = gl.getActiveUniform(program, i);
    if (!info) continue;
    const name = info.name.replace(/\[0\]$/, '');
    uniforms[name] = gl.getUniformLocation(program, name);
  }
  return uniforms;
}

function reflectAttrib(gl, program, names) {
  for (const name of names) {
    const loc = gl.getAttribLocation(program, name);
    if (loc !== -1) return loc;
  }
  return -1;
}

function createTexture(gl) {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return tex;
}

function createRenderTarget(gl, w, h, float) {
  const texture = createTexture(gl);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    float ? gl.RGBA32F : gl.RGBA8,
    w,
    h,
    0,
    gl.RGBA,
    float ? gl.FLOAT : gl.UNSIGNED_BYTE,
    null,
  );

  const fbo = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    gl.deleteTexture(texture);
    gl.deleteFramebuffer(fbo);
    throw new Error(`framebuffer incomplete (0x${status.toString(16)}), float=${float}`);
  }
  return { texture, fbo, width: w, height: h, float };
}

/** One compiled RetroArch-style shader pass: a program plus its reflected interface. */
class Pass {
  constructor(gl, rawSource, label, { secondarySamplerName = null } = {}) {
    this.gl = gl;
    this.label = label;
    this.program = buildProgram(gl, rawSource, label);
    this.uniforms = reflectUniforms(gl, this.program);
    this.aVertexCoord = reflectAttrib(gl, this.program, ['VertexCoord']);
    this.aTexCoord = reflectAttrib(gl, this.program, ['TexCoord']);
    this.aColor = reflectAttrib(gl, this.program, ['Color', 'COLOR']);

    // Shader authors here are inconsistent about the primary sampler's name
    // (xbr-lv2 uses "decal"; everything else uses "Texture").
    this.primaryName = ['Texture', 'decal'].find((n) => n in this.uniforms) ?? null;
    this.secondaryName = secondarySamplerName && secondarySamplerName in this.uniforms ? secondarySamplerName : null;

    gl.useProgram(this.program);
    if (this.primaryName) gl.uniform1i(this.uniforms[this.primaryName], 0);
    if (this.secondaryName) gl.uniform1i(this.uniforms[this.secondaryName], 1);
  }

  draw({ primary, secondary, outWidth, outHeight, framebuffer, frameCount }) {
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.viewport(0, 0, outWidth, outHeight);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, primary.texture);
    if (secondary && this.secondaryName) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, secondary.texture);
    }

    const set2 = (name, x, y) => {
      const loc = this.uniforms[name];
      if (loc) gl.uniform2f(loc, x, y);
    };

    if (this.uniforms.MVPMatrix) gl.uniformMatrix4fv(this.uniforms.MVPMatrix, false, IDENTITY4);
    if (this.uniforms.FrameDirection) gl.uniform1i(this.uniforms.FrameDirection, 1);
    if (this.uniforms.FrameCount) gl.uniform1i(this.uniforms.FrameCount, frameCount | 0);
    set2('OutputSize', outWidth, outHeight);
    set2('TextureSize', primary.width, primary.height);
    set2('InputSize', primary.width, primary.height);
    if (secondary && this.secondaryName) {
      set2(`${this.secondaryName}Size`, secondary.width, secondary.height);
      set2(`${this.secondaryName.replace('Texture', '')}InputSize`, secondary.width, secondary.height);
    }

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
}

export class GLFilterRenderer {
  constructor(canvas) {
    this.canvas = canvas;
    this.frameCount = 0;
    this.failures = {};
    this._passes = {};
    this._rt = new Map();

    // preserveDrawingBuffer is required for the canvas to be readable (a
    // screenshot, a user's right-click "save image", or a test harness
    // sampling pixels) at any point other than immediately inside the same
    // task that drew it -- without it the browser is free to clear the
    // buffer right after compositing each frame.
    this.gl = canvas.getContext('webgl2', { alpha: false, antialias: false, preserveDrawingBuffer: true });
    if (!this.gl) {
      this.failures.__context = 'WebGL2 is not available in this browser.';
      return;
    }
    const gl = this.gl;

    this._quadPos = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this._quadPos);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);

    this._quadTex = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, this._quadTex);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);

    this.sourceTexture = createTexture(gl);

    this._tryBuild('xbr', () => new Pass(gl, xbrSrc, 'xbr'));
    this._tryBuild('xbrz', () => new Pass(gl, xbrzSrc, 'xbrz'));
    this._tryBuild('sabr', () => new Pass(gl, sabrSrc, 'sabr'));

    this._floatExt = gl.getExtension('EXT_color_buffer_float');
    if (this._floatExt) {
      this._tryBuild('scalefx0', () => new Pass(gl, sfx0Src, 'scalefx-pass0'));
      this._tryBuild('scalefx1', () => new Pass(gl, sfx1Src, 'scalefx-pass1'));
      this._tryBuild('scalefx2', () => new Pass(gl, sfx2Src, 'scalefx-pass2', { secondarySamplerName: 'PassPrev2Texture' }));
      this._tryBuild('scalefx3', () => new Pass(gl, sfx3Src, 'scalefx-pass3'));
      this._tryBuild('scalefx4', () => new Pass(gl, sfx4Src, 'scalefx-pass4', { secondarySamplerName: 'PassPrev5Texture' }));
    } else {
      this.failures.scalefx = 'ScaleFX needs the EXT_color_buffer_float WebGL extension, which this browser/device does not support.';
    }
  }

  _tryBuild(name, factory) {
    try {
      this._passes[name] = factory();
    } catch (err) {
      this.failures[name] = err?.message ?? String(err);
      console.error(`[gl-filters] ${name} unavailable:`, err);
    }
  }

  get available() {
    return !!this.gl;
  }

  isFilterReady(id) {
    if (!this.available) return false;
    if (id === 'scalefx') {
      return ['scalefx0', 'scalefx1', 'scalefx2', 'scalefx3', 'scalefx4'].every((n) => this._passes[n]);
    }
    return !!this._passes[id];
  }

  failureFor(id) {
    if (this.failures.__context) return this.failures.__context;
    if (id === 'scalefx') {
      return this.failures.scalefx ?? this.failures.scalefx0 ?? this.failures.scalefx2 ?? this.failures.scalefx4 ?? null;
    }
    return this.failures[id] ?? null;
  }

  _uploadSource(sourceCanvas) {
    const gl = this.gl;
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.bindTexture(gl.TEXTURE_2D, this.sourceTexture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, sourceCanvas);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  }

  _bindQuad(pass) {
    const gl = this.gl;
    if (pass.aVertexCoord !== -1) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this._quadPos);
      gl.enableVertexAttribArray(pass.aVertexCoord);
      gl.vertexAttribPointer(pass.aVertexCoord, 2, gl.FLOAT, false, 0, 0);
    }
    if (pass.aTexCoord !== -1) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this._quadTex);
      gl.enableVertexAttribArray(pass.aTexCoord);
      gl.vertexAttribPointer(pass.aTexCoord, 2, gl.FLOAT, false, 0, 0);
    }
    if (pass.aColor !== -1) {
      gl.disableVertexAttribArray(pass.aColor);
      gl.vertexAttrib4f(pass.aColor, 1, 1, 1, 1);
    }
  }

  _getRenderTarget(key, w, h, float) {
    const existing = this._rt.get(key);
    if (existing && existing.width === w && existing.height === h && existing.float === float) return existing;
    if (existing) {
      this.gl.deleteTexture(existing.texture);
      this.gl.deleteFramebuffer(existing.fbo);
    }
    const rt = createRenderTarget(this.gl, w, h, float);
    this._rt.set(key, rt);
    return rt;
  }

  /**
   * Render one frame through `filterId`.
   *
   * xbr/xbrz/sabr fill exactly outWidth x outHeight (they compute their own
   * per-output-pixel sampling, like the canvas 2D filters do). ScaleFX's
   * last pass is fixed at 3x the source by the algorithm itself, so it
   * ignores outWidth/outHeight and the caller should fit the returned size
   * to the display the same way the "sharp-bilinear" 2D filter does.
   *
   * @returns {{width: number, height: number}} the pixel size actually rendered.
   */
  render(sourceCanvas, filterId, sourceW, sourceH, outWidth, outHeight) {
    if (!this.isFilterReady(filterId)) {
      throw new Error(this.failureFor(filterId) ?? `${filterId} is not available`);
    }
    const gl = this.gl;
    this.frameCount++;
    this._uploadSource(sourceCanvas);
    const source = { texture: this.sourceTexture, width: sourceW, height: sourceH };

    if (filterId !== 'scalefx') {
      const pass = this._passes[filterId];
      const w = Math.max(1, Math.round(outWidth));
      const h = Math.max(1, Math.round(outHeight));
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
      }
      this._bindQuad(pass);
      pass.draw({ primary: source, outWidth: w, outHeight: h, framebuffer: null, frameCount: this.frameCount });
      return { width: w, height: h };
    }

    // ScaleFX: passes 0-1 write float "metric"/"strength" data (unbounded,
    // not colour), 2-3 write regular RGBA8 "junction"/"tag" data, and pass 4
    // composites the final 3x image from pass 3's tags + the ORIGINAL source.
    const p0 = this._getRenderTarget('sfx0', sourceW, sourceH, true);
    const p1 = this._getRenderTarget('sfx1', sourceW, sourceH, true);
    const p2 = this._getRenderTarget('sfx2', sourceW, sourceH, false);
    const p3 = this._getRenderTarget('sfx3', sourceW, sourceH, false);

    const runPass = (name, primary, secondary, target) => {
      const pass = this._passes[name];
      this._bindQuad(pass);
      pass.draw({
        primary,
        secondary,
        outWidth: target ? target.width : this.canvas.width,
        outHeight: target ? target.height : this.canvas.height,
        framebuffer: target ? target.fbo : null,
        frameCount: this.frameCount,
      });
    };

    const asSource = (rt) => ({ texture: rt.texture, width: sourceW, height: sourceH });

    runPass('scalefx0', source, null, p0);
    runPass('scalefx1', asSource(p0), null, p1);
    runPass('scalefx2', asSource(p1), asSource(p0), p2);
    runPass('scalefx3', asSource(p2), null, p3);

    const w = sourceW * 3;
    const h = sourceH * 3;
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    runPass('scalefx4', asSource(p3), source, null);

    return { width: w, height: h };
  }

  dispose() {
    if (!this.gl) return;
    const gl = this.gl;
    for (const rt of this._rt.values()) {
      gl.deleteTexture(rt.texture);
      gl.deleteFramebuffer(rt.fbo);
    }
    for (const pass of Object.values(this._passes)) gl.deleteProgram(pass.program);
    gl.deleteTexture(this.sourceTexture);
    gl.deleteBuffer(this._quadPos);
    gl.deleteBuffer(this._quadTex);
  }
}
